<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useApiHealth } from "@/queries/health";
import type { AppHealthCheck, SystemInfo } from "@/types/health-types";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";


const { apiHealth, apiHealthEntries, loading, refetchApiHealth, dataUpdatedAt } = useApiHealth();

// Browser-side checks — not API calls, managed locally
const appHealthChecks = ref<AppHealthCheck[]>([]);
const systemInfo = ref<SystemInfo | null>(null);

// UI state
const autoRefresh = ref(true);
const refreshInterval = ref(30);
const refreshTimer = ref<ReturnType<typeof setInterval> | null>(null);
const customRefreshInterval = ref(30);

// Derived overall status from API + browser checks
const overallStatus = computed<"Healthy" | "Warning" | "Error">(() => {
  if (!apiHealth.value && appHealthChecks.value.length === 0) return "Error";
  const hasApiError = apiHealth.value?.status === "Unhealthy";
  const hasAppError = appHealthChecks.value.some((c) => c.status === "Error");
  const hasWarning =
    apiHealth.value?.status === "Degraded" ||
    appHealthChecks.value.some((c) => c.status === "Warning");
  if (hasApiError || hasAppError) return "Error";
  if (hasWarning) return "Warning";
  return "Healthy";
});

// Status colors and icons
const statusConfig = computed<
  Record<string, { class: string; icon: string }>
>(() => ({
  Healthy: { class: "success", icon: "bi-check-circle-fill" },
  Warning: { class: "warning", icon: "bi-exclamation-triangle-fill" },
  Error: { class: "danger", icon: "bi-x-circle-fill" },
  Degraded: { class: "warning", icon: "bi-exclamation-triangle-fill" },
  Unhealthy: { class: "danger", icon: "bi-x-circle-fill" },
}));

const overallStatusConfig = computed(() => {
  const status = overallStatus.value;
  return statusConfig.value[status as keyof typeof statusConfig.value];
});

const formatDuration = (duration: string | number) => {
  if (typeof duration === "string") return duration;
  return `${duration.toFixed(2)}ms`;
};

const formatMemory = (bytes: number) => {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const timeAgo = computed(() => {
  if (!dataUpdatedAt.value) return "Never";
  const seconds = Math.floor((Date.now() - dataUpdatedAt.value) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
});

async function runAppHealthChecks() {
  const serverBaseUrl = import.meta.env.VITE_API_URL.replace(/\/api$/, "");
  const checks: AppHealthCheck[] = [];

  // Local Storage check
  try {
    const start = performance.now();
    localStorage.setItem("health_check_test", "test");
    localStorage.removeItem("health_check_test");
    const duration = performance.now() - start;
    checks.push({
      name: "Local Storage",
      status: duration < 10 ? "Healthy" : "Warning",
      message: duration < 10 ? "Working normally" : "Slow response",
      duration,
      timestamp: new Date(),
      details: { responseTime: `${duration.toFixed(2)}ms` },
    });
  } catch (e) {
    checks.push({ name: "Local Storage", status: "Error", message: "Local storage unavailable", duration: 0, timestamp: new Date(), details: { error: e instanceof Error ? e.message : "Unknown" } });
  }

  // API connectivity check
  try {
    const start = performance.now();
    const response = await fetch(`${serverBaseUrl}/health`, { method: "HEAD", credentials: "include", headers: { Accept: "application/json, text/plain, */*" } });
    const duration = performance.now() - start;
    const isAuthError = response.url.includes("/login") || response.status === 401;
    checks.push({
      name: "API Connectivity",
      status: isAuthError ? "Warning" : response.ok ? (duration < 1000 ? "Healthy" : "Warning") : "Error",
      message: isAuthError ? "Authentication required" : response.ok ? (duration < 1000 ? "Connected" : "Slow connection") : `HTTP ${response.status}`,
      duration,
      timestamp: new Date(),
      details: { responseTime: `${duration.toFixed(2)}ms`, status: response.status, statusText: response.statusText, authRequired: isAuthError },
    });
  } catch (e) {
    checks.push({ name: "API Connectivity", status: "Error", message: "Cannot reach API", duration: 0, timestamp: new Date(), details: { error: e instanceof Error ? e.message : "Network error" } });
  }

  // Memory check (Chrome only)
  if ("memory" in performance) {
    const memory = (performance as any).memory;
    const usagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
    checks.push({
      name: "Memory Usage",
      status: usagePercent < 70 ? "Healthy" : usagePercent < 90 ? "Warning" : "Error",
      message: `${usagePercent.toFixed(1)}% used`,
      duration: 0,
      timestamp: new Date(),
      details: { used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`, total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`, limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB` },
    });
  }

  // Network status
  checks.push({ name: "Network Status", status: navigator.onLine ? "Healthy" : "Error", message: navigator.onLine ? "Online" : "Offline", duration: 0, timestamp: new Date(), details: { online: navigator.onLine } });

  appHealthChecks.value = checks;
}

function collectSystemInfo() {
  const connection = (navigator as any).connection;
  systemInfo.value = {
    userAgent: navigator.userAgent,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    memory: "memory" in performance ? { used: (performance as any).memory.usedJSHeapSize, total: (performance as any).memory.totalJSHeapSize, limit: (performance as any).memory.jsHeapSizeLimit } : undefined,
    connection: connection ? { effectiveType: `${connection.effectiveType?.toUpperCase() || "Unknown"} (${connection.downlink?.toFixed(1) || "?"} Mbps)`, rtt: connection.rtt, downlink: connection.downlink } : undefined,
    online: navigator.onLine,
  };
}

async function runFullHealthCheck() {
  await Promise.all([refetchApiHealth(), runAppHealthChecks()]);
  collectSystemInfo();
}

function startAutoRefresh() {
  if (refreshTimer.value) clearInterval(refreshTimer.value);
  if (autoRefresh.value) {
    refreshTimer.value = setInterval(() => runFullHealthCheck(), refreshInterval.value * 1000);
  }
}

function toggleAutoRefresh() {
  autoRefresh.value = !autoRefresh.value;
  startAutoRefresh();
}

function updateRefreshInterval() {
  refreshInterval.value = Math.max(5, Math.min(300, customRefreshInterval.value));
  startAutoRefresh();
}

function refreshNow() {
  runFullHealthCheck();
}

const apiBaseUrl = import.meta.env.VITE_API_URL;
// Remove trailing /api from the base URL for scalar and health endpoints
const serverBaseUrl = apiBaseUrl.replace(/\/api$/, "");

function openScalarDocs() {
  window.open(`${serverBaseUrl}/scalar/v1`, "_blank");
}

function openApiHealth() {
  window.open(`${serverBaseUrl}/health`, "_blank");
}

onMounted(() => {
  runFullHealthCheck();
  startAutoRefresh();
});

onUnmounted(() => {
  if (refreshTimer.value) {
    clearInterval(refreshTimer.value);
  }
});
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <PageHeader
          title="Site Health"
          :breadcrumbs="[
            { label: 'Dashboard', href: '/' },
            { label: 'Site Health' }
          ]"
        >
          <template #actions>
            <button
              class="bb-btn-secondary-cta btn-sm"
              @click="refreshNow"
              :disabled="loading"
            >
              <i class="bi bi-arrow-clockwise me-1" :class="{ spin: loading }"></i>
              Refresh
            </button>
            <button class="bb-btn-secondary-cta btn-sm" @click="openScalarDocs">
              <i class="bi bi-book me-1"></i> API Docs
            </button>
          </template>
        </PageHeader>

        <!-- Overall Status Card -->
        <div class="row g-4 mb-4">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <h2 class="card-title fw-semibold">System Status</h2>
                <div class="row align-items-center">
                  <div class="col-auto">
                    <div
                      class="status-icon-large"
                      :class="`text-${overallStatusConfig.class}`"
                    >
                      <i :class="overallStatusConfig.icon"></i>
                    </div>
                  </div>
                  <div class="col">
                    <h3 class="mb-1">
                      System Status:
                      <span :class="`text-${overallStatusConfig.class}`">{{
                        overallStatus
                      }}</span>
                    </h3>
                    <p class="text-muted mb-0">
                      Last updated: {{ timeAgo }}
                      <span v-if="autoRefresh">
                        • Auto-refresh every {{ refreshInterval }}s</span
                      >
                    </p>
                  </div>
                  <div class="col-auto">
                    <div class="d-flex align-items-center gap-3">
                      <div class="d-flex align-items-center gap-2">
                        <label class="form-label mb-0 small"
                          >Interval (s):</label
                        >
                        <input
                          type="number"
                          class="form-control form-control-sm refresh-interval-input"
                          v-model="customRefreshInterval"
                          @change="updateRefreshInterval"
                          min="5"
                          max="300"
                        />
                      </div>
                      <button
                        class="bb-btn-secondary-cta btn-sm"
                        @click="toggleAutoRefresh"
                      >
                        <i :class="autoRefresh ? 'bi bi-pause me-1' : 'bi bi-play me-1'"></i>
                        {{ autoRefresh ? "Pause" : "Start" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Health Check Cards -->
        <div class="row g-4">
          <!-- API Health Card -->
          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-body">
                <div
                  class="d-flex justify-content-between align-items-center mb-3"
                >
                  <h2 class="card-title mb-0 fw-semibold">
                    <i class="bi bi-cloud me-2"></i>API Health
                  </h2>
                  <button
                    class="bb-btn-secondary-cta btn-sm"
                    @click="openApiHealth"
                  >
                    <i class="bi bi-box-arrow-up-right me-1"></i> View Raw
                  </button>
                </div>
                <div>
                  <div v-if="loading && !apiHealth" class="text-center py-4">
                    <div class="spinner-border spinner-border-sm me-2"></div>
                    Checking API health...
                  </div>

                  <div v-else-if="apiHealth">
                    <div class="d-flex align-items-center mb-3">
                      <div
                        class="status-indicator me-3"
                        :class="`text-${statusConfig[apiHealth.status].class}`"
                      >
                        <i :class="statusConfig[apiHealth.status].icon"></i>
                      </div>
                      <div>
                        <h3 class="mb-0">{{ apiHealth.status }}</h3>
                        <small class="text-muted"
                          >Total duration: {{ apiHealth.totalDuration }}</small
                        >
                      </div>
                    </div>

                    <div
                      v-if="apiHealthEntries.length > 0"
                      class="health-entries"
                    >
                      <div
                        v-for="[key, entry] in apiHealthEntries"
                        :key="key"
                        class="health-entry p-2 mb-2 bg-body-tertiary rounded"
                      >
                        <div
                          class="d-flex justify-content-between align-items-center"
                        >
                          <div class="d-flex align-items-center">
                            <i
                              :class="[
                                statusConfig[entry.status].icon,
                                `text-${statusConfig[entry.status].class}`,
                                'me-2',
                              ]"
                            ></i>
                            <span class="fw-medium">{{ key }}</span>
                          </div>
                          <small class="text-muted">{{
                            formatDuration(entry.duration)
                          }}</small>
                        </div>
                        <div v-if="entry.description" class="ms-4">
                          <small class="text-muted">{{
                            entry.description
                          }}</small>
                        </div>
                        <div v-if="entry.exception" class="ms-4">
                          <small class="text-danger">{{
                            entry.exception
                          }}</small>
                        </div>
                      </div>
                    </div>

                    <div v-else class="text-muted text-center py-3">
                      No detailed health entries available
                    </div>
                  </div>

                  <div v-else class="text-center py-4 text-muted">
                    <i class="bi bi-exclamation-circle display-6"></i>
                    <p class="mt-2">API health data unavailable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- App Health Card -->
          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title mb-3 fw-semibold">
                  <i class="bi bi-laptop me-2"></i>Application Health
                </h2>
                <div>
                  <div v-if="appHealthChecks.length > 0">
                    <div
                      v-for="check in appHealthChecks"
                      :key="check.name"
                      class="p-3 mb-3 bg-body-tertiary rounded"
                    >
                      <div
                        class="d-flex justify-content-between align-items-start"
                      >
                        <div class="d-flex align-items-center flex-grow-1">
                          <i
                            :class="[
                              statusConfig[check.status].icon,
                              `text-${statusConfig[check.status].class}`,
                              'me-2',
                            ]"
                          ></i>
                          <div class="flex-grow-1">
                            <h3 class="mb-0">{{ check.name }}</h3>
                            <small class="text-muted">{{
                              check.message
                            }}</small>
                          </div>
                        </div>
                        <small class="text-muted ms-2">
                          {{
                            check.duration > 0
                              ? formatDuration(check.duration)
                              : ""
                          }}
                        </small>
                      </div>

                      <div v-if="check.details" class="ms-4 mt-1">
                        <small class="text-muted">
                          <span
                            v-for="(value, key) in check.details"
                            :key="key"
                            class="me-3"
                          >
                            {{ key }}: {{ value }}
                          </span>
                        </small>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center py-4 text-muted">
                    <i class="bi bi-hourglass-split display-6"></i>
                    <p class="mt-2">Running health checks...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- System Information Card -->
          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title mb-3 fw-semibold">
                  <i class="bi bi-info-circle me-2"></i>System Information
                </h2>
                <div>
                  <div v-if="systemInfo">
                    <div class="d-flex flex-column gap-3">
                      <div class="d-flex align-items-center">
                        <strong>Browser:</strong>
                        <span class="ms-2">{{
                          systemInfo.userAgent.split(" ")[0]
                        }}</span>
                      </div>

                      <div class="d-flex align-items-center">
                        <strong>Viewport:</strong>
                        <span class="ms-2"
                          >{{ systemInfo.viewport.width }}×{{
                            systemInfo.viewport.height
                          }}</span
                        >
                      </div>

                      <div class="d-flex align-items-center">
                        <strong>Online Status:</strong>
                        <span
                          class="ms-2"
                          :class="
                            systemInfo.online ? 'text-success' : 'text-danger'
                          "
                        >
                          {{ systemInfo.online ? "Connected" : "Offline" }}
                        </span>
                      </div>

                      <div v-if="systemInfo.memory" class="d-flex align-items-center">
                        <strong>Memory Usage:</strong>
                        <span class="ms-2"
                          >{{ formatMemory(systemInfo.memory.used) }} /
                          {{ formatMemory(systemInfo.memory.limit) }}</span
                        >
                      </div>

                      <div v-if="systemInfo.connection" class="d-flex align-items-center">
                        <strong>Connection:</strong>
                        <span class="ms-2">{{
                          systemInfo.connection.effectiveType
                        }}</span>
                        <span
                          v-if="systemInfo.connection.rtt"
                          class="ms-2 text-muted"
                        >
                          ({{ systemInfo.connection.rtt }}ms RTT)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center py-4 text-muted">
                    <i class="bi bi-gear display-6"></i>
                    <p class="mt-2">Collecting system information...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Links Card -->
          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title mb-3 fw-semibold">
                  <i class="bi bi-link-45deg me-2"></i>Quick Links
                </h2>
                <div>
                  <div class="d-grid gap-2">
                    <a
                      :href="`${serverBaseUrl}/scalar/v1`"
                      target="_blank"
                      class="bb-btn-secondary-cta d-flex align-items-center"
                    >
                      <i class="bi bi-book me-2"></i>
                      API Documentation (Scalar)
                      <i class="bi bi-box-arrow-up-right ms-auto"></i>
                    </a>

                    <a
                      :href="`${serverBaseUrl}/health`"
                      target="_blank"
                      class="bb-btn-secondary-cta d-flex align-items-center"
                    >
                      <i class="bi bi-heart-pulse me-2"></i>
                      Raw API Health Endpoint
                      <i class="bi bi-box-arrow-up-right ms-auto"></i>
                    </a>

                    <button
                      class="bb-btn-primary-cta d-flex align-items-center"
                      @click="refreshNow"
                      :disabled="loading"
                    >
                      <i
                        class="bi bi-arrow-clockwise me-2"
                        :class="{ spin: loading }"
                      ></i>
                      Run Full Health Check
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PanelCard>
    </PanelGrid>
  </main>
</template>

<style scoped lang="scss">
/* Minimal custom styles - most styling uses Bootstrap classes */

.refresh-interval-input {
  width: 70px;
}

.status-icon-large {
  font-size: 3rem;
}

.status-indicator {
  font-size: 1.25rem;
}

.health-entry {
  border-left: 3px solid var(--bs-border-color);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .status-icon-large {
    font-size: 2rem;
  }
}
</style>
